/**
 * Device-capability classifier for BUG-0008.
 *
 * ── What it measures, and why not a generic benchmark ────────────────────
 *
 * ONE FULL DETECT CYCLE of the same work the scan performs: the canvas read, a
 * MoveNet round-trip through the worker, and periodically the main-thread
 * EfficientDet pass. What the app requires of a device is not FLOPS, it is that
 * this specific chain completes inside a specific budget.
 *
 * `deviceMemory` / `hardwareConcurrency` (already on `app.pageload`) are
 * deliberately NOT used as the classifier: both are null on all of iOS,
 * `deviceMemory` is spec-bucketed to six values, and neither describes the GPU
 * path MoveNet actually runs on. They stay on `app.pageload` as context for
 * reading these results, not as inputs to them.
 *
 * ── Where the thresholds come from ───────────────────────────────────────
 *
 *   125ms = 8 Hz. `docs/telemetry/EVENTS.md` defines `effectivePoseHz < 8` as
 *           "the device could not keep up", independently of this bug, and the
 *           dossier's signature is built on it.
 *   250ms = `CountdownManager`'s tick interval — the only hard real-time
 *           deadline in the app. 44 ticks are emitted on a fixed setInterval
 *           whether or not the previous detect finished, so a device past this
 *           falls progressively behind during rotation.
 *
 * NEITHER IS VALIDATED AGAINST OUTCOMES YET. The join that would validate them
 * — measured cycle time against whether that journey reached
 * `scan.rotation.summary` with `reachedSuccess` — does not exist until this
 * ships and lands next to `effectivePoseHz` in `scan.alignment.summary`. Until
 * then nothing branches on the verdict. See the plan §2.3 for how to re-pick
 * them from that data.
 *
 * ── The fail-safe direction ──────────────────────────────────────────────
 *
 * `unknown` maps to the STANDARD path, never to assisted. An unclassified
 * device on today's behaviour carries a known risk (it may hit BUG-0008); an
 * unclassified device on a looser gate carries an avatar-quality risk that
 * surfaces as a wrong avatar rather than as an error, because the backend has
 * no runtime pose validator on the PWA path.
 *
 * Plan: docs/superpowers/plans/2026-09-02-bug-0008-low-end-device-alignment-fix-plan.md §2
 */

import { BUILD_TAG } from "@/generated/get-build-tag";
import type { DeviceClass } from "./alignmentTuning";
import type { BenchmarkResult } from "./benchmarkScore";

/** The first detects pay WebGL shader compilation and MediaPipe graph setup. */
export const WARMUP_CYCLES_DISCARDED = 2;
export const MEASURED_CYCLES = 8;
/**
 * A device that cannot finish its MEASURED cycles inside this is below ~1.3 Hz.
 * The discarded cycles are outside it (see `beginClassification`).
 */
export const CLASSIFY_TIMEOUT_MS = 6000;
export const ASSISTED_THRESHOLD_MS = 125;
export const MARGINAL_THRESHOLD_MS = 250;

const CACHE_KEY = "ttf:deviceClass:" + BUILD_TAG;

export interface DeviceMetrics {
  cycleMedianMs: number | null;
  /**
   * The SLOWEST of the measured cycles, and named that way on purpose.
   *
   * With MEASURED_CYCLES = 8 there is no input for which a 90th percentile
   * differs from the maximum (`floor(0.9 * 8) = 7`, the last index), so calling
   * it `p90` would invite whoever re-picks the thresholds to reason about a
   * tail statistic while looking at a max-of-8. Two of the eight measured
   * cycles carry the EfficientDet pass and are systematically the slow ones, so
   * in practice this is "the slower of the two object-detection cycles".
   *
   * Classification runs off it deliberately — a fixed 250 ms rotation tick has
   * to be met by the WORST cycles, not the median ones — but that choice is one
   * of the things to re-examine alongside the thresholds themselves, because it
   * also means a single GC pause can move a device a class.
   */
  cycleMaxMs: number | null;
  /** The isolated main-thread EfficientDet cost — the lever the stride pulls. */
  objectPassMs: number | null;
  deviceClass: DeviceClass;
  measuredCycles: number;
  /**
   * How this verdict was reached. Only `measured` and `timeout` are DEVICE
   * verdicts; `yielded`, `error` and `deferred` say something about the run,
   * not the handset, and all three carry `deviceClass: "unknown"`.
   */
  source:
    | "measured"
    | "cached"
    | "timeout"
    | "error"
    | "deferred"
    /** Abandoned because a real scan started and needs the worker. */
    | "yielded";
  /** The live rate overrode the boot verdict. Recorded so telemetry can split. */
  promotedLive: boolean;
  /**
   * The real-frame benchmark's verdict (2026-09-11 spec §4.4): speed alone
   * cannot see a phone that is fast but wrong. `null` = no benchmark ran.
   */
  benchmark: BenchmarkResult | null;
}

const UNMEASURED: DeviceMetrics = {
  cycleMedianMs: null,
  cycleMaxMs: null,
  objectPassMs: null,
  deviceClass: "unknown",
  measuredCycles: 0,
  source: "deferred",
  promotedLive: false,
  benchmark: null,
};

let samples: number[] = [];
let discarded = 0;
let objectPassMs: number | null = null;
/**
 * `null`, not 0: `performance.now()` can legitimately return 0, and a sentinel
 * that collides with a real reading makes the timeout branch unreachable on
 * exactly the run that starts at time zero.
 */
let startedAt: number | null = null;
let metrics: DeviceMetrics = UNMEASURED;

/**
 * Start the run's clock and return its origin, so the caller's budget check and
 * `finishClassification`'s `elapsed` read the same one.
 *
 * `recordCycle` also stamps it lazily, but it is first called AFTER cycle 0's
 * detect resolves — and cycle 0 is systematically the slowest, paying the WebGL
 * shader compile and the MediaPipe graph setup. That left `elapsed` short by
 * exactly the slowest cycle: at 700 ms cycles the loop breaks on its own budget
 * at t≈6300 while `elapsed` reads 5600, so the run fell into the `error` branch
 * — `unknown`, not persisted, mapped to the standard path — and the `timeout` →
 * `marginal` verdict the slowest handsets exist for was unreachable. `EVENTS.md`
 * reads a high `error` share as "the classifier is throwing on some platform",
 * so those devices were being misread as a platform bug.
 *
 * Re-stamps on every call: `yielded` and `error` runs are deliberately not
 * cached, so the next attempt must get its own budget rather than an already
 * expired one. The startup check also calls it a second time once its
 * discarded cycles are done, so the budget covers the MEASURED cycles only
 * (`PoseDetectionManager.runBenchmarkCycles`).
 */
export function beginClassification(): number {
  startedAt = performance.now();
  return startedAt;
}

export function recordCycle(ms: number): void {
  // Fallback origin for a caller that did not call `beginClassification`. The
  // startup check re-stamps the origin after its discarded cycles: a phone that
  // spends 6 s on them has NOT answered the question — prod iPhones spend a
  // median 14 s on a cold first detection and then run 9-38 ms cycles.
  if (startedAt === null) startedAt = performance.now();
  if (discarded < WARMUP_CYCLES_DISCARDED) {
    discarded++;
    return;
  }
  if (samples.length < MEASURED_CYCLES) samples.push(ms);
}

export function recordObjectPass(ms: number): void {
  objectPassMs = Math.round(ms);
}

/** True median over a sorted copy. Runs once, off the hot loop. */
function median(sorted: number[]): number {
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function classify(worstCycleMs: number): DeviceClass {
  if (worstCycleMs <= ASSISTED_THRESHOLD_MS) return "standard";
  if (worstCycleMs <= MARGINAL_THRESHOLD_MS) return "assisted";
  return "marginal";
}

/**
 * @param abortedBecause `"yielded"` when the run was abandoned because a real
 *   scan started. That is a fact about the USER's timing, not about the
 *   handset, so it must never produce a device verdict — least of all
 *   `marginal`, which is the class Release B renders a "this phone can't
 *   complete a scan" banner for.
 * @param benchmark The real-frame benchmark's result, carried on every verdict
 *   so the session cache keeps it (spec §4.4). `null` when none ran.
 */
export function finishClassification(
  abortedBecause?: "yielded",
  benchmark: BenchmarkResult | null = null
): DeviceMetrics {
  const elapsed = startedAt === null ? 0 : performance.now() - startedAt;

  if (abortedBecause === "yielded") {
    metrics = {
      ...UNMEASURED,
      measuredCycles: samples.length,
      source: "yielded",
      benchmark,
    };
    return metrics; // not persisted — see `persist`
  }

  if (samples.length < MEASURED_CYCLES) {
    metrics =
      elapsed >= CLASSIFY_TIMEOUT_MS
        ? {
            ...UNMEASURED,
            // Reachable only via the loop's own elapsed check: the run was
            // still going at the budget, which IS a device verdict — a handset
            // that cannot finish 10 cycles in 6s is below ~1.7 Hz.
            deviceClass: "marginal",
            measuredCycles: samples.length,
            source: "timeout",
            benchmark,
          }
        : {
            ...UNMEASURED,
            measuredCycles: samples.length,
            source: "error",
            benchmark,
          };
    if (metrics.source === "timeout") persist(metrics);
    return metrics;
  }

  const sorted = samples.slice().sort((a, b) => a - b);
  const worstCycleMs = sorted[sorted.length - 1];
  metrics = {
    cycleMedianMs: Math.round(median(sorted)),
    cycleMaxMs: Math.round(worstCycleMs),
    objectPassMs,
    // The worst cycle rather than the mean: a device that stalls one cycle in
    // eight cannot hold a 250ms rotation tick, and the mean hides exactly that.
    deviceClass: classify(worstCycleMs),
    measuredCycles: samples.length,
    source: "measured",
    promotedLive: false,
    benchmark,
  };
  persist(metrics);
  return metrics;
}

/**
 * Session-scoped and build-tagged. NEVER `localStorage`: thermal state and
 * background load change between sessions, and a stale PASS is the more
 * dangerous of the two stale verdicts.
 *
 * Only called for real device verdicts (`measured`, `timeout`). Caching a
 * `yielded` or `error` run would make `shouldClassify()` refuse to try again
 * for the rest of the session, so one unlucky moment would cost the device its
 * measurement entirely.
 */
function persist(m: DeviceMetrics): void {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(m));
  } catch {
    /* Private-mode Safari throws on sessionStorage access. The verdict is
       still live in memory for this page load, which is all it is needed for. */
  }
}

function readCache(): DeviceMetrics | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DeviceMetrics;
    if (!parsed || typeof parsed.deviceClass !== "string") return null;
    // A verdict cached by an older build has no `benchmark`.
    return { ...parsed, source: "cached", benchmark: parsed.benchmark || null };
  } catch {
    return null;
  }
}

/**
 * False while the tab is hidden — a throttled tab measures the OS, not the
 * handset, which is why the dossier excludes `outcome: backgrounded` from its
 * own signature — and false when this build already has a verdict for this
 * session. Callers re-arm on the next `visibilitychange -> visible`.
 */
export function shouldClassify(): boolean {
  if (
    typeof document !== "undefined" &&
    document.visibilityState !== "visible"
  ) {
    return false;
  }
  const cached = readCache();
  if (cached) {
    metrics = cached;
    return false;
  }
  return true;
}

export function getDeviceMetrics(): DeviceMetrics {
  if (metrics.source === "deferred") {
    const cached = readCache();
    if (cached) metrics = cached;
  }
  return metrics;
}

export function getDeviceClass(): DeviceClass {
  return getDeviceMetrics().deviceClass;
}

/**
 * The live rate overrides the boot verdict, in the WIDENING direction only.
 *
 * Demotion is deliberately not offered here: narrowing the gate under a user
 * who is standing still would take away a gate they were about to satisfy, and
 * that is the one thing the union gate in `PoseDetectionManager` promises
 * cannot happen. Callers demote by re-reading the boot verdict at an attempt
 * boundary instead.
 */
export function promoteToAssisted(reason: string): void {
  const current = getDeviceMetrics();
  if (current.deviceClass === "standard" || current.deviceClass === "unknown") {
    // Keeps cycleMedianMs/cycleMaxMs and `source` so telemetry can still join the
    // boot measurement against the outcome.
    metrics = {
      ...current,
      deviceClass: "assisted",
      promotedLive: true,
    };
    void reason;
  }
}

/** Test-only. */
export function __resetDeviceCapabilityForTest(): void {
  samples = [];
  discarded = 0;
  objectPassMs = null;
  startedAt = null;
  metrics = UNMEASURED;
}
